import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Input, Tag, Typography } from "antd";
import {
  AudioOutlined,
  AppstoreOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useChat } from "../../context/ChatContext";
import { DriverContext } from "../../context/DriverContext";
import { useFleetAssistant } from "../../context/FleetAssistantContext";
import { db, storage } from "../../firebase";
import {
  API_FleetAssistant_chat,
  API_FleetAssistant_history,
  API_REQUEST_HEADERS_BASEURL,
} from "../../utils/Constants";
import "./FleetAssistantPanel.css";

const DEFAULT_ASSISTANT_NAME = "Fleet Assistant";
const HISTORY_PAGE_SIZE = 24;
const INITIAL_ASSISTANT_MESSAGE_ID = "fleet-assistant-welcome";

const messageId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalizeId = (value) => String(value || "").trim();
const mergeComposerText = (...parts) =>
  parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

const getSpeechRecognitionConstructor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const getSpeechRecognitionLanguage = (languageCode = "en") => {
  const normalized = String(languageCode || "").trim().toLowerCase();
  if (normalized.includes("-")) return normalized;
  const languageMap = {
    en: "en-US",
    es: "es-ES",
    ca: "ca-ES",
    de: "de-DE",
    fr: "fr-FR",
    ru: "ru-RU",
    pl: "pl-PL",
  };
  return languageMap[normalized] || "en-US";
};

function buildAssistantMessage(content = "", id = messageId()) {
  return {
    id,
    role: "assistant",
    content: String(content || "").trim(),
    charts: [],
    attachments: [],
  };
}

function toHistory(messages = []) {
  return messages
    .filter((entry) => entry?.role === "assistant" || entry?.role === "user")
    .slice(-12)
    .map((entry) => ({
      role: entry.role,
      content: String(entry.content || ""),
    }));
}

function fromPersistedHistory(messages = []) {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((entry) => entry && typeof entry === "object")
    .map((entry, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      role: entry.role === "assistant" ? "assistant" : "user",
      content: String(entry.content || "").trim(),
      charts: [],
      attachments: [],
    }))
    .filter((entry) => entry.content);
}

export default function FleetAssistantPanel() {
  const { getFleetID, user } = useContext(AuthContext);
  const { driversDetails } = useContext(DriverContext);
  const { openChat } = useChat();
  const { closeAssistant } = useFleetAssistant();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const fleetId = getFleetID();
  const isWelcomePage = location.pathname.startsWith("/welcome");
  const defaultAssistantName = t("fleetAssistant.defaultName", { defaultValue: DEFAULT_ASSISTANT_NAME });
  const initialAssistantMessage = useMemo(
    () =>
      buildAssistantMessage(
        t("fleetAssistant.welcomeMessage", {
          defaultValue:
            "Fleet Assistant is ready. I can help with operations stats, dashboard customization, and client-side actions.",
        }),
        INITIAL_ASSISTANT_MESSAGE_ID
      ),
    [t]
  );

  const [messages, setMessages] = useState([initialAssistantMessage]);
  const [visibleMessageCount, setVisibleMessageCount] = useState(HISTORY_PAGE_SIZE);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isCustomizationPanelOpen, setCustomizationPanelOpen] = useState(false);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [isUpdatingDashboardWidgets, setUpdatingDashboardWidgets] = useState(false);
  const [assistantName, setAssistantName] = useState(defaultAssistantName);
  const [assistantNameDraft, setAssistantNameDraft] = useState(defaultAssistantName);
  const [isEditingAssistantName, setEditingAssistantName] = useState(false);
  const [isSavingAssistantName, setSavingAssistantName] = useState(false);
  const [isSpeechRecognitionActive, setSpeechRecognitionActive] = useState(false);

  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const assistantNameInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const composerInputRef = useRef(null);
  const expandingHistoryRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const speechRecognitionRef = useRef(null);
  const speechBaseDraftRef = useRef("");
  const speechFinalTranscriptRef = useRef("");
  const speechCancelRequestedRef = useRef(false);
  const speechSuppressAutoSendRef = useRef(false);
  const speechSkipDraftUpdateOnEndRef = useRef(false);
  const isSpeechRecognitionActiveRef = useRef(false);
  const speechRecognitionSupported = useMemo(() => Boolean(getSpeechRecognitionConstructor()), []);

  const focusComposerInput = useCallback(() => {
    requestAnimationFrame(() => {
      const inputRef = composerInputRef.current;
      if (inputRef?.focus) {
        try {
          inputRef.focus({ cursor: "end" });
          return;
        } catch {
          inputRef.focus();
          return;
        }
      }
      inputRef?.resizableTextArea?.textArea?.focus?.();
    });
  }, []);

  const displayedMessages = useMemo(() => {
    const parsedCount = Number(visibleMessageCount);
    const count = Math.max(1, Number.isFinite(parsedCount) ? parsedCount : HISTORY_PAGE_SIZE);
    return messages.slice(-count);
  }, [messages, visibleMessageCount]);
  const hasMoreHistory = displayedMessages.length < messages.length;

  const driverMap = useMemo(() => {
    const map = {};
    for (const driver of driversDetails || []) {
      map[normalizeId(driver?.id)] = driver;
    }
    return map;
  }, [driversDetails]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (expandingHistoryRef.current) {
      const addedHeight = container.scrollHeight - previousScrollHeightRef.current;
      container.scrollTop = Math.max(0, container.scrollTop + addedHeight);
      expandingHistoryRef.current = false;
      previousScrollHeightRef.current = 0;
      return;
    }

    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [displayedMessages.length, pendingAction, isSending]);

  useEffect(() => {
    focusComposerInput();
  }, [focusComposerInput]);

  useEffect(() => {
    isSpeechRecognitionActiveRef.current = isSpeechRecognitionActive;
  }, [isSpeechRecognitionActive]);

  useEffect(() => {
    if (!isEditingAssistantName) return;
    requestAnimationFrame(() => {
      const nameInput = assistantNameInputRef.current;
      if (!nameInput?.focus) return;
      try {
        nameInput.focus({
          cursor: "all",
        });
      } catch {
        nameInput.focus();
      }
    });
  }, [isEditingAssistantName]);

  useEffect(() => {
    if (!fleetId) {
      setMessages([initialAssistantMessage]);
      setVisibleMessageCount(1);
    }
  }, [fleetId, initialAssistantMessage]);

  useEffect(
    () => () => {
      const recognition = speechRecognitionRef.current;
      if (!recognition) return;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // Ignore: abort can throw if recognition is already inactive.
      }
      speechRecognitionRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (isWelcomePage) return;
    setCustomizationPanelOpen(false);
  }, [isWelcomePage]);

  useEffect(() => {
    if (!fleetId || !isWelcomePage) {
      setDashboardWidgets([]);
      return undefined;
    }

    const customizationRef = doc(db, "fleets", fleetId, "customizations", "welcome_dashboard");
    const unsubscribe = onSnapshot(
      customizationRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
        setDashboardWidgets(widgets);
      },
      (error) => {
        console.error("FleetAssistant customization panel load failed:", error);
        setDashboardWidgets([]);
      }
    );

    return () => unsubscribe();
  }, [fleetId, isWelcomePage]);

  useEffect(() => {
    if (!fleetId) {
      setAssistantName(defaultAssistantName);
      setAssistantNameDraft(defaultAssistantName);
      setEditingAssistantName(false);
      setMessages([initialAssistantMessage]);
      setVisibleMessageCount(1);
      return undefined;
    }

    const assistantRef = doc(db, "fleets", fleetId, "assistant", "main");
    const unsubscribe = onSnapshot(
      assistantRef,
      (snapshot) => {
        const remoteName = String(snapshot?.data()?.displayName || "").trim();
        const nextName = remoteName || defaultAssistantName;
        setAssistantName(nextName);
        if (!isEditingAssistantName) {
          setAssistantNameDraft(nextName);
        }
      },
      (error) => {
        console.error("FleetAssistant name load failed:", error);
        setAssistantName(defaultAssistantName);
        if (!isEditingAssistantName) {
          setAssistantNameDraft(defaultAssistantName);
        }
      }
    );

    return () => unsubscribe();
  }, [defaultAssistantName, fleetId, isEditingAssistantName]);

  useEffect(() => {
    if (!fleetId || !user?.uid) {
      setMessages([initialAssistantMessage]);
      setVisibleMessageCount(1);
      return undefined;
    }

    let isCancelled = false;
    const loadHistory = async () => {
      try {
        const endpoint = API_FleetAssistant_history.replace("{fleetId}", fleetId);
        const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint);
        const persistedHistory = fromPersistedHistory(response?.data?.data || []);
        if (isCancelled) return;
        if (persistedHistory.length > 0) {
          setMessages(persistedHistory);
          setVisibleMessageCount(Math.min(persistedHistory.length, HISTORY_PAGE_SIZE));
        } else {
          setMessages([initialAssistantMessage]);
          setVisibleMessageCount(1);
        }
      } catch (error) {
        if (isCancelled) return;
        console.error("FleetAssistant history load failed:", error);
        setMessages([initialAssistantMessage]);
        setVisibleMessageCount(1);
      }
    };

    void loadHistory();
    return () => {
      isCancelled = true;
    };
  }, [fleetId, initialAssistantMessage, user?.uid]);

  const saveDashboardWidgets = useCallback(
    async (widgets = []) => {
      if (!fleetId) return;
      setUpdatingDashboardWidgets(true);
      try {
        await setDoc(
          doc(db, "fleets", fleetId, "customizations", "welcome_dashboard"),
          {
            widgets: Array.isArray(widgets) ? widgets : [],
            updatedAt: new Date(),
            type: "dashboard",
          },
          { merge: true }
        );
      } catch (error) {
        console.error("FleetAssistant customization panel save failed:", error);
      } finally {
        setUpdatingDashboardWidgets(false);
      }
    },
    [fleetId]
  );

  const saveAssistantName = useCallback(async () => {
    const nextName = String(assistantNameDraft || "").trim().slice(0, 80) || defaultAssistantName;
    if (nextName === assistantName) {
      setAssistantNameDraft(nextName);
      setEditingAssistantName(false);
      return;
    }
    if (!fleetId) return;
    setSavingAssistantName(true);
    try {
      await setDoc(
        doc(db, "fleets", fleetId, "assistant", "main"),
        {
          displayName: nextName,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setAssistantName(nextName);
      setEditingAssistantName(false);
    } catch (error) {
      console.error("FleetAssistant name save failed:", error);
    } finally {
      setSavingAssistantName(false);
    }
  }, [assistantName, assistantNameDraft, defaultAssistantName, fleetId]);

  const getWidgetLabel = useCallback((widget, index) => {
    const title = String(widget?.title || "").trim();
    if (title) return title;
    if (widget?.type === "kpi") {
      return t("fleetAssistant.widgetKpiFallback", {
        defaultValue: "KPI - {{value}}",
        value: String(widget?.metricKey || `metric-${index + 1}`),
      });
    }
    if (widget?.type === "chart") {
      return t("fleetAssistant.widgetChartFallback", {
        defaultValue: "Chart - {{value}}",
        value: String(widget?.chartKey || `chart-${index + 1}`),
      });
    }
    return t("fleetAssistant.widgetFallback", {
      defaultValue: "Widget {{index}}",
      index: index + 1,
    });
  }, [t]);

  const handleRemoveDashboardWidget = useCallback(
    async (widget, index) => {
      const widgetId = String(widget?.id || "").trim();
      const nextWidgets = dashboardWidgets.filter((entry, entryIndex) => {
        if (widgetId) {
          return String(entry?.id || "").trim() !== widgetId;
        }
        return entryIndex !== index;
      });
      await saveDashboardWidgets(nextWidgets);
    },
    [dashboardWidgets, saveDashboardWidgets]
  );

  const handleRemoveAllDashboardWidgets = useCallback(async () => {
    await saveDashboardWidgets([]);
  }, [saveDashboardWidgets]);

  const uploadAttachments = useCallback(
    async (files) => {
      if (!fleetId || !Array.isArray(files) || files.length === 0) return [];
      const uploaded = [];
      const MAX_ASSISTANT_UPLOAD_BYTES = 10 * 1024 * 1024;
      for (const file of files) {
        const mimeType = String(file?.type || "").trim().toLowerCase();
        if (!mimeType.startsWith("image/")) {
          throw new Error(`Unsupported assistant attachment type: ${file?.type || "unknown"}`);
        }
        if (Number(file?.size || 0) > MAX_ASSISTANT_UPLOAD_BYTES) {
          throw new Error(`Assistant attachment exceeds ${MAX_ASSISTANT_UPLOAD_BYTES} bytes`);
        }
        const safeName = String(file?.name || "attachment").replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `fleets/${fleetId}/assistant_uploads/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: mimeType });
        const url = await getDownloadURL(storageRef);
        uploaded.push({
          name: file.name || safeName,
          mimeType: file.type || "",
          size: Number(file.size || 0),
          url,
          path,
        });
      }
      return uploaded;
    },
    [fleetId]
  );

  const executeClientActions = useCallback(
    (actions = []) => {
      if (!Array.isArray(actions) || actions.length === 0) return;

      const withAssistantParams = (basePath, params = {}) => {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          const normalized = String(value || "").trim();
          if (!normalized) return;
          search.set(key, normalized);
        });
        const suffix = search.toString();
        return suffix ? `${basePath}?${suffix}` : basePath;
      };

      for (const action of actions) {
        const type = String(action?.type || "").trim();
        const payload = action?.payload || {};

        if (type === "NAVIGATE") {
          const path = String(payload.path || "").trim();
          if (path) navigate(path);
          continue;
        }

        if (type === "CLEAR_ASSISTANT_HISTORY") {
          const assistantReply = String(payload.assistantReply || "").trim();
          if (assistantReply) {
            setMessages([
              {
                id: messageId(),
                role: "assistant",
                content: assistantReply,
                charts: [],
                attachments: [],
              },
            ]);
            setVisibleMessageCount(1);
          } else {
            setMessages([initialAssistantMessage]);
            setVisibleMessageCount(1);
          }
          setPendingAction(null);
          focusComposerInput();
          continue;
        }

        if (type === "OPEN_DRIVER_DETAILS") {
          const driverId = normalizeId(payload.driverId);
          if (driverId) {
            navigate(
              withAssistantParams("/drivers", {
                assistantDriverId: driverId,
                assistantDriverDetail: "1",
              })
            );
          }
          continue;
        }

        if (type === "OPEN_DRIVER_CHAT") {
          const driverId = normalizeId(payload.driverId);
          const fallbackName = String(payload.driverName || "Driver");
          const driver = driverMap[driverId] || null;
          const name = driver?.name || fallbackName;

          if (driverId) {
            openChat({
              id: driverId,
              name,
              type: "All",
              currentOrder: payload.currentOrder || driver?.currentOrder || null,
              driverStatus: driver?.isOnline ? "online" : "offline",
              profilePic: driver?.profilePic || null,
            });
          }
          continue;
        }

        if (type === "OPEN_ORDER_DETAILS") {
          const orderId = String(payload.orderId || "").trim();
          const orderRef = String(payload.orderRef || "").trim();
          if (orderId) {
            navigate(
              withAssistantParams("/transports", {
                assistantOrderId: orderId,
                assistantOrderRef: orderRef,
                assistantOrderDetail: "1",
              })
            );
          }
          continue;
        }

        if (type === "OPEN_ORDER_CHAT") {
          const orderId = String(payload.orderId || "").trim();
          const orderRef = String(payload.orderRef || "").trim();
          if (orderId) {
            navigate(
              withAssistantParams("/transports", {
                assistantOrderId: orderId,
                assistantOrderRef: orderRef,
                assistantOrderChat: "1",
              })
            );
          }
          continue;
        }

        if (type === "OPEN_CREATE_ORDER_MODAL") {
          navigate(
            withAssistantParams("/transports", {
              assistantCreateOrder: "1",
            })
          );
        }
      }
    },
    [driverMap, focusComposerInput, initialAssistantMessage, navigate, openChat]
  );

  const callAssistant = useCallback(
    async ({ message, attachments = [], action = null, approved = null }) => {
      if (!fleetId) {
        return {
          reply: t("fleetAssistant.errorFleetNotSelected", { defaultValue: "Fleet not selected." }),
          pendingAction: null,
          clientActions: [],
        };
      }

      const endpoint = API_FleetAssistant_chat.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint, {
        message,
        history: toHistory(messages),
        attachments,
        pendingAction: action,
        approved,
        pageContext: {
          path: location.pathname,
          search: location.search || "",
          assistantName,
        },
      });
      return response?.data?.data || {};
    },
    [assistantName, fleetId, location.pathname, location.search, messages, t]
  );

  const pushAssistantMessage = useCallback((payload) => {
    const content = String(payload?.reply || "").trim();
    if (!content) return;
    setMessages((prev) => [
      ...prev,
      buildAssistantMessage(content || t("fleetAssistant.done", { defaultValue: "Done." })),
    ]);
  }, [t]);

  const stopSpeechRecognition = useCallback(
    ({ cancel = false, suppressAutoSend = false, skipDraftUpdate = false } = {}) => {
      const recognition = speechRecognitionRef.current;
      if (!recognition) return;
      if (cancel) {
        speechCancelRequestedRef.current = true;
      }
      if (suppressAutoSend) {
        speechSuppressAutoSendRef.current = true;
      }
      if (skipDraftUpdate) {
        speechSkipDraftUpdateOnEndRef.current = true;
      }
      try {
        if (cancel) {
          recognition.abort();
        } else {
          recognition.stop();
        }
      } catch (error) {
        console.error("FleetAssistant speech recognition stop failed:", error);
        speechCancelRequestedRef.current = false;
        speechSuppressAutoSendRef.current = false;
        speechSkipDraftUpdateOnEndRef.current = false;
        isSpeechRecognitionActiveRef.current = false;
        setSpeechRecognitionActive(false);
      }
    },
    []
  );

  const handleSend = useCallback(
    async (options = {}) => {
      const skipSpeechStop = options?.skipSpeechStop === true;
      const messageOverride = options?.messageOverride;

      if (!skipSpeechStop && isSpeechRecognitionActiveRef.current) {
        stopSpeechRecognition({ suppressAutoSend: true, skipDraftUpdate: true });
      }
      const message = String(messageOverride ?? draft ?? "").trim();
      if (!message && selectedFiles.length === 0) return;

      setIsSending(true);
      setPendingAction(null);
      const localFiles = [...selectedFiles];
      setSelectedFiles([]);
      setDraft("");

      setMessages((prev) => [
        ...prev,
        {
          id: messageId(),
          role: "user",
          content: message || t("fleetAssistant.attachmentFallback", { defaultValue: "(attachment)" }),
          charts: [],
          attachments: localFiles.map((file) => ({ name: file.name || "attachment" })),
        },
      ]);

      try {
        const uploadedAttachments = await uploadAttachments(localFiles);
        const payload = await callAssistant({
          message,
          attachments: uploadedAttachments,
        });
        setPendingAction(payload.pendingAction || null);
        pushAssistantMessage(payload);
        executeClientActions(payload.clientActions || []);
      } catch (error) {
        console.error("FleetAssistant send error:", error);
        setSelectedFiles(localFiles);
        setMessages((prev) => [
          ...prev,
          buildAssistantMessage(
            t("fleetAssistant.errorProcessRequest", {
              defaultValue: "I couldn't process that request right now.",
            })
          ),
        ]);
      } finally {
        setIsSending(false);
        focusComposerInput();
      }
    },
    [
      callAssistant,
      draft,
      executeClientActions,
      focusComposerInput,
      pushAssistantMessage,
      selectedFiles,
      stopSpeechRecognition,
      t,
      uploadAttachments,
    ]
  );

  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognitionConstructor = getSpeechRecognitionConstructor();
    if (!SpeechRecognitionConstructor) return false;

    const recognition =
      speechRecognitionRef.current || new SpeechRecognitionConstructor();
    speechRecognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = getSpeechRecognitionLanguage(i18n.resolvedLanguage || i18n.language || "en");

    speechBaseDraftRef.current = String(draft || "").trim();
    speechFinalTranscriptRef.current = "";
    speechCancelRequestedRef.current = false;
    speechSuppressAutoSendRef.current = false;
    speechSkipDraftUpdateOnEndRef.current = false;

    recognition.onresult = (event) => {
      let nextFinalTranscript = speechFinalTranscriptRef.current;
      let nextInterimTranscript = "";

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = String(result?.[0]?.transcript || "").trim();
        if (!transcript) continue;
        if (result.isFinal) {
          nextFinalTranscript = mergeComposerText(nextFinalTranscript, transcript);
        } else {
          nextInterimTranscript = transcript;
        }
      }

      speechFinalTranscriptRef.current = nextFinalTranscript;
      setDraft(mergeComposerText(speechBaseDraftRef.current, nextFinalTranscript, nextInterimTranscript));
    };

    recognition.onerror = (event) => {
      console.error("FleetAssistant speech recognition error:", event);
      if (String(event?.error || "") === "aborted") return;
      speechSuppressAutoSendRef.current = true;
    };

    recognition.onend = () => {
      isSpeechRecognitionActiveRef.current = false;
      setSpeechRecognitionActive(false);
      const baseDraft = speechBaseDraftRef.current;
      const finalTranscript = String(speechFinalTranscriptRef.current || "").trim();
      const mergedDraft = mergeComposerText(baseDraft, finalTranscript);
      const shouldCancel = speechCancelRequestedRef.current;
      const shouldAutoSend = !shouldCancel && !speechSuppressAutoSendRef.current && Boolean(finalTranscript);
      const skipDraftUpdate = speechSkipDraftUpdateOnEndRef.current;

      speechCancelRequestedRef.current = false;
      speechSuppressAutoSendRef.current = false;
      speechSkipDraftUpdateOnEndRef.current = false;
      speechFinalTranscriptRef.current = "";

      if (shouldCancel) {
        setDraft(baseDraft);
        focusComposerInput();
        return;
      }

      if (shouldAutoSend) {
        void handleSend({
          skipSpeechStop: true,
          messageOverride: mergedDraft,
        });
        return;
      }

      if (skipDraftUpdate) {
        focusComposerInput();
        return;
      }

      setDraft(mergedDraft);
      focusComposerInput();
    };

    try {
      recognition.start();
      setSpeechRecognitionActive(true);
      return true;
    } catch (error) {
      console.error("FleetAssistant speech recognition start failed:", error);
      isSpeechRecognitionActiveRef.current = false;
      setSpeechRecognitionActive(false);
      return false;
    }
  }, [draft, focusComposerInput, handleSend, i18n.language, i18n.resolvedLanguage]);

  const handleSpeechRecognitionToggle = useCallback(() => {
    if (isSending) return;
    if (isSpeechRecognitionActiveRef.current) {
      stopSpeechRecognition();
      return;
    }
    if (!speechRecognitionSupported) return;
    startSpeechRecognition();
  }, [isSending, speechRecognitionSupported, startSpeechRecognition, stopSpeechRecognition]);

  const handlePendingDecision = useCallback(
    async (approved) => {
      if (!pendingAction || isSending) return;

      setIsSending(true);
      setMessages((prev) => [
        ...prev,
        {
          id: messageId(),
          role: "user",
          content: approved
            ? t("fleetAssistant.confirmMessage", { defaultValue: "Confirm." })
            : t("fleetAssistant.cancelMessage", { defaultValue: "Cancel." }),
          charts: [],
          attachments: [],
        },
      ]);

      try {
        const payload = await callAssistant({
          message: approved ? "confirm" : "cancel",
          attachments: [],
          action: pendingAction,
          approved,
        });
        setPendingAction(payload.pendingAction || null);
        pushAssistantMessage(payload);
        executeClientActions(payload.clientActions || []);
      } catch (error) {
        console.error("FleetAssistant confirm error:", error);
        setMessages((prev) => [
          ...prev,
          buildAssistantMessage(
            t("fleetAssistant.errorCompleteConfirmation", {
              defaultValue: "I couldn't complete that confirmation.",
            })
          ),
        ]);
      } finally {
        setIsSending(false);
        focusComposerInput();
      }
    },
    [
      callAssistant,
      executeClientActions,
      focusComposerInput,
      isSending,
      pendingAction,
      pushAssistantMessage,
      t,
    ]
  );

  const handleMessagesScroll = useCallback(
    (event) => {
      const container = event.currentTarget;
      if (!container || expandingHistoryRef.current) return;
      if (!hasMoreHistory) return;
      if (container.scrollTop > 24) return;

      previousScrollHeightRef.current = container.scrollHeight;
      expandingHistoryRef.current = true;
      setVisibleMessageCount((previous) => Math.min(messages.length, previous + HISTORY_PAGE_SIZE));
    },
    [hasMoreHistory, messages.length]
  );

  return (
    <>
      <aside className="fleet-assistant-panel">
        <header className="fleet-assistant-panel__header">
          <div className="fleet-assistant-panel__header-row">
            <div className="fleet-assistant-panel__title">
              <Avatar icon={<RobotOutlined />} size="small" />
              <div className="fleet-assistant-panel__title-content">
                {isEditingAssistantName ? (
                  <Input
                    ref={assistantNameInputRef}
                    size="small"
                    value={assistantNameDraft}
                    maxLength={80}
                    className="fleet-assistant-panel__title-input"
                    onChange={(event) => setAssistantNameDraft(event.target.value)}
                    onPressEnter={() => {
                      if (!isSavingAssistantName) {
                        void saveAssistantName();
                      }
                    }}
                    onBlur={() => {
                      if (!isSavingAssistantName) {
                        void saveAssistantName();
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") return;
                      event.preventDefault();
                      setAssistantNameDraft(assistantName || defaultAssistantName);
                      setEditingAssistantName(false);
                    }}
                    placeholder={defaultAssistantName}
                  />
                ) : (
                  <button
                    type="button"
                    className="fleet-assistant-panel__title-button"
                    title={t("fleetAssistant.rename", { defaultValue: "Rename" })}
                    onClick={() => {
                      setAssistantNameDraft(assistantName || defaultAssistantName);
                      setEditingAssistantName(true);
                    }}
                  >
                    <Typography.Text strong>{assistantName}</Typography.Text>
                    <EditOutlined />
                  </button>
                )}
                <div className="fleet-assistant-panel__subtitle">
                  {t("fleetAssistant.subtitle", { defaultValue: "Operations + dashboard copilot" })}
                </div>
              </div>
            </div>
            <div className="fleet-assistant-panel__header-actions">
              {isWelcomePage ? (
                <Button
                  size="small"
                  icon={<AppstoreOutlined />}
                  type={isCustomizationPanelOpen ? "primary" : "default"}
                  title={t("fleetAssistant.customizations", { defaultValue: "Customizations" })}
                  aria-label={t("fleetAssistant.customizations", { defaultValue: "Customizations" })}
                  onClick={() => setCustomizationPanelOpen((prev) => !prev)}
                />
              ) : null}
              <Button
                size="small"
                icon={<CloseOutlined />}
                title={t("fleetAssistant.close", { defaultValue: "Close Fleet Assistant" })}
                aria-label={t("fleetAssistant.close", { defaultValue: "Close Fleet Assistant" })}
                onClick={closeAssistant}
              />
            </div>
          </div>
        </header>

        <section
          ref={messagesContainerRef}
          className="fleet-assistant-panel__messages"
          onScroll={handleMessagesScroll}
        >
          {hasMoreHistory ? (
            <div className="fleet-assistant-panel__history-hint">
              {t("fleetAssistant.loadOlderHint", { defaultValue: "Scroll up to load older messages" })}
            </div>
          ) : null}
          {displayedMessages.map((messageItem) => {
            const isAssistant = messageItem.role === "assistant";
            return (
              <div
                key={messageItem.id}
                className={`fleet-assistant-message ${
                  isAssistant ? "fleet-assistant-message--assistant" : "fleet-assistant-message--user"
                }`}
              >
                <div className="fleet-assistant-message__icon">
                  {isAssistant ? <RobotOutlined /> : <UserOutlined />}
                </div>
                <div className="fleet-assistant-message__body">
                  <div className="fleet-assistant-message__bubble">{messageItem.content}</div>
                  {Array.isArray(messageItem.attachments) && messageItem.attachments.length > 0 ? (
                    <div className="fleet-assistant-message__attachments">
                      {messageItem.attachments.map((attachment, index) => (
                        <Tag key={`${messageItem.id}-attachment-${index}`}>{attachment.name}</Tag>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          {isSending ? (
            <div className="fleet-assistant-message fleet-assistant-message--assistant">
              <div className="fleet-assistant-message__icon">
                <RobotOutlined />
              </div>
              <div className="fleet-assistant-message__body">
                <div className="fleet-assistant-message__bubble">
                  <LoadingOutlined />{" "}
                  {t("fleetAssistant.thinking", {
                    defaultValue: "Thinking...",
                  })}
                </div>
              </div>
            </div>
          ) : null}
          <div ref={endRef} />
        </section>

        {pendingAction ? (
          <div className="fleet-assistant-panel__confirm">
            <Typography.Text type="secondary">
              {pendingAction.requiresConfirmation
                ? t("fleetAssistant.confirmationRequired", { defaultValue: "Confirmation required." })
                : t("fleetAssistant.pendingAction", { defaultValue: "Pending action." })}
            </Typography.Text>
            <div className="fleet-assistant-panel__confirm-buttons">
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="small"
                onClick={() => handlePendingDecision(true)}
                disabled={isSending}
              >
                {t("fleetAssistant.confirm", { defaultValue: "Confirm" })}
              </Button>
              <Button
                icon={<CloseOutlined />}
                size="small"
                onClick={() => handlePendingDecision(false)}
                disabled={isSending}
              >
                {t("fleetAssistant.cancel", { defaultValue: "Cancel" })}
              </Button>
            </div>
          </div>
        ) : null}

        <footer className="fleet-assistant-panel__composer">
          {selectedFiles.length > 0 ? (
            <div className="fleet-assistant-panel__attachments-preview">
              {selectedFiles.map((file, index) => (
                <Tag
                  key={`${file.name}-${index}`}
                  closable
                  onClose={() =>
                    setSelectedFiles((prev) => prev.filter((_, innerIndex) => innerIndex !== index))
                  }
                >
                  {file.name}
                </Tag>
              ))}
            </div>
          ) : null}

          <div className="fleet-assistant-panel__composer-row">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length > 0) {
                  setSelectedFiles((prev) => [...prev, ...files]);
                }
                event.target.value = "";
              }}
            />

            <Button
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
            />
            <Input.TextArea
              ref={composerInputRef}
              value={draft}
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder={t("fleetAssistant.composerPlaceholder", {
                defaultValue: "Ask for fleet stats, dashboard updates, or operational actions...",
              })}
              onChange={(event) => setDraft(event.target.value)}
              onPressEnter={(event) => {
                if (event.shiftKey) return;
                event.preventDefault();
                handleSend();
              }}
              disabled={isSending}
            />
            <Button type="primary" icon={<SendOutlined />} onClick={handleSend} disabled={isSending} />
            <div className="fleet-assistant-panel__voice-controls">
              <Button
                className={
                  isSpeechRecognitionActive ? "fleet-assistant-panel__mic-btn--listening" : undefined
                }
                type={isSpeechRecognitionActive ? "primary" : "default"}
                icon={
                  isSpeechRecognitionActive ? (
                    <span className="fleet-assistant-panel__mic-icon-wave" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  ) : (
                    <AudioOutlined />
                  )
                }
                onClick={handleSpeechRecognitionToggle}
                disabled={isSending || (!speechRecognitionSupported && !isSpeechRecognitionActive)}
                title={
                  !speechRecognitionSupported
                    ? t("fleetAssistant.voiceInputUnsupported", {
                        defaultValue: "Voice input is not available in this browser.",
                      })
                    : isSpeechRecognitionActive
                      ? t("fleetAssistant.stopVoiceInput", {
                          defaultValue: "Stop recording and send",
                        })
                      : t("fleetAssistant.startVoiceInput", { defaultValue: "Start voice input" })
                }
                aria-label={
                  !speechRecognitionSupported
                    ? t("fleetAssistant.voiceInputUnsupported", {
                        defaultValue: "Voice input is not available in this browser.",
                      })
                    : isSpeechRecognitionActive
                      ? t("fleetAssistant.stopVoiceInput", {
                          defaultValue: "Stop recording and send",
                        })
                      : t("fleetAssistant.startVoiceInput", { defaultValue: "Start voice input" })
                }
              />
              {isSpeechRecognitionActive ? (
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => stopSpeechRecognition({ cancel: true, suppressAutoSend: true })}
                  disabled={isSending}
                  title={t("fleetAssistant.cancel", { defaultValue: "Cancel" })}
                  aria-label={t("fleetAssistant.cancel", { defaultValue: "Cancel" })}
                />
              ) : null}
            </div>
          </div>
        </footer>
      </aside>

      {isWelcomePage && isCustomizationPanelOpen ? (
        <aside className="fleet-assistant-customization-panel">
          <header className="fleet-assistant-customization-panel__header">
            <Typography.Text strong>
              {t("fleetAssistant.welcomeCustomizations", { defaultValue: "Welcome Customizations" })}
            </Typography.Text>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setCustomizationPanelOpen(false)}
            />
          </header>
          <section className="fleet-assistant-customization-panel__body">
            {dashboardWidgets.length === 0 ? (
              <Typography.Text type="secondary">
                {t("fleetAssistant.noDashboardCustomizations", {
                  defaultValue: "No dashboard customizations.",
                })}
              </Typography.Text>
            ) : (
              <div className="fleet-assistant-customization-panel__list">
                {dashboardWidgets.map((widget, index) => {
                  const label = getWidgetLabel(widget, index);
                  const widgetKey = `${String(widget?.id || widget?.type || "widget")}-${index}`;
                  return (
                    <div className="fleet-assistant-customization-panel__item" key={widgetKey}>
                      <Typography.Text className="fleet-assistant-customization-panel__item-label">
                        {label}
                      </Typography.Text>
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={isUpdatingDashboardWidgets}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleRemoveDashboardWidget(widget, index);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          <footer className="fleet-assistant-customization-panel__footer">
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={dashboardWidgets.length === 0 || isUpdatingDashboardWidgets}
              loading={isUpdatingDashboardWidgets}
              onClick={() => void handleRemoveAllDashboardWidgets()}
              block
            >
              {t("fleetAssistant.removeAll", { defaultValue: "Remove all" })}
            </Button>
          </footer>
        </aside>
      ) : null}
    </>
  );
}
